import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { Plus, Minus, AlertCircle } from "lucide-react";
import { Button } from "../ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { ParticipantFormSection } from "./ParticipantFormSection";
import { RegistrationFooter } from "./RegistrationFooter";
import {
  buildRegistrationSchema,
  type RegistrationFormData,
} from "../../lib/schemas/registration";
import type { Event } from "../../lib/types";
import type { AccountUser } from "../../lib/hooks/useAccountUsers";
import { getBookingStatus } from "../../lib/utils";
import { TEXT_CONTENT } from "../../lib/text-content";

interface RegistrationFormProps {
  event: Event;
  allAccountUsers: AccountUser[];
  /** Tiers that can be booked (excludes full). */
  availablePricePoints: NonNullable<Event["pricePoints"]>;
  /** All tiers in the booking window for the category dropdown (includes full as disabled). */
  categoryPricePoints: NonNullable<Event["pricePoints"]>;
  bookingOpen: boolean;
  onSubmit: (data: RegistrationFormData, stayOnPage: boolean) => Promise<void>;
  isSubmitting: boolean;
  currency: string;
}

export function RegistrationForm({
  event,
  allAccountUsers,
  availablePricePoints,
  categoryPricePoints,
  bookingOpen,
  onSubmit,
  isSubmitting,
  currency,
}: RegistrationFormProps) {
  const bookablePricePointIds = useMemo(
    () => new Set(availablePricePoints.map((p) => p.id)),
    [availablePricePoints],
  );

  const registrationResolver = useMemo(
    () => zodResolver(buildRegistrationSchema(bookablePricePointIds)),
    [bookablePricePointIds],
  );

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm<RegistrationFormData>({
    resolver: registrationResolver,
    defaultValues: {
      quantity: 1,
      participants: [
        {
          firstName: "",
          lastName: "",
          userId: "other",
          pricePointId: "",
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "participants",
  });

  const quantity = watch("quantity");
  const participants = watch("participants");

  const canChangeQuantity =
    bookingOpen && availablePricePoints.length > 0;

  // Sync participants array with quantity
  useEffect(() => {
    const currentLength = fields.length;
    if (quantity > currentLength) {
      const needed = quantity - currentLength;
      const defaultPricePointId = availablePricePoints[0]?.id ?? "";
      
      for (let i = 0; i < needed; i++) {
        append({
          firstName: "",
          lastName: "",
          userId: "other",
          pricePointId: defaultPricePointId,
        });
      }
    } else if (quantity < currentLength) {
      const toRemove = currentLength - quantity;
      // Remove from the end
      for (let i = 0; i < toRemove; i++) {
        remove(currentLength - 1 - i);
      }
    }
  }, [quantity, fields.length, append, remove, availablePricePoints]);

  // Default to first bookable tier; clear selections that point at a full tier
  useEffect(() => {
    const firstBookable = availablePricePoints[0]?.id ?? "";
    getValues("participants").forEach((p, index) => {
      if (
        p.pricePointId &&
        !bookablePricePointIds.has(p.pricePointId) &&
        firstBookable
      ) {
        setValue(`participants.${index}.pricePointId`, firstBookable);
      } else if (!p.pricePointId && firstBookable) {
        setValue(`participants.${index}.pricePointId`, firstBookable);
      }
    });
  }, [availablePricePoints, bookablePricePointIds, getValues, setValue]);

  const calculateTotal = () => {
    return participants.reduce((total, p) => {
      const pricePoint = availablePricePoints.find(
        (pp) => pp.id === p.pricePointId,
      );
      return total + (pricePoint?.priceCents || 0);
    }, 0);
  };

  const handleQuantityChange = (newQuantity: number) => {
    setValue("quantity", newQuantity);
  };

  const onUserSelect = (index: number, userId: string) => {
    setValue(`participants.${index}.userId`, userId);
    
    if (userId !== "other") {
      const selectedUser = allAccountUsers.find((u) => u.id === userId);
      if (selectedUser) {
        setValue(`participants.${index}.firstName`, selectedUser.firstName);
        setValue(`participants.${index}.lastName`, selectedUser.lastName);
      }
    } else {
      setValue(`participants.${index}.firstName`, "");
      setValue(`participants.${index}.lastName`, "");
    }
  };

  if (event.isCancelled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Registration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-2 text-sm text-red-700">
            <AlertCircle className="w-4 h-4 mt-0.5" />
            <div>
              <div className="font-medium">{TEXT_CONTENT.EVENTS.CANCELLED_TITLE}</div>
              <div>{TEXT_CONTENT.EVENTS.CANCELLED_DESC}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const bookingCapacityStatus = getBookingStatus(
    event.pricePoints,
    event.bookingClosesAt,
    event.startsAt,
  );
  const isFullyBookedNoPlaces =
    bookingOpen &&
    availablePricePoints.length === 0 &&
    bookingCapacityStatus === "full";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Register for this event</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Number of Participants
          </label>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleQuantityChange(Math.max(1, quantity - 1))}
              disabled={!canChangeQuantity || quantity <= 1}
            >
              <Minus className="w-4 h-4" />
            </Button>
            <span className="w-12 text-center font-medium">{quantity}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleQuantityChange(Math.min(10, quantity + 1))}
              disabled={!canChangeQuantity || quantity >= 10}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Display prominent error banner if validation errors exist */}
        {errors.participants && (
          <div className="p-4 bg-red-50 border-2 border-red-300 rounded-md">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-800 mb-1">
                  Please fix the following errors:
                </p>
                <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                  {Array.isArray(errors.participants) ? (
                    errors.participants.map((participantError, index) => {
                      const errorMessages: string[] = [];
                      if (participantError?.firstName?.message) {
                        errorMessages.push(
                          `Participant ${index + 1}: ${participantError.firstName.message}`
                        );
                      }
                      if (participantError?.lastName?.message) {
                        errorMessages.push(
                          `Participant ${index + 1}: ${participantError.lastName.message}`
                        );
                      }
                      if (participantError?.pricePointId?.message) {
                        errorMessages.push(
                          `Participant ${index + 1}: ${participantError.pricePointId.message}`
                        );
                      }
                      return errorMessages.map((msg, msgIndex) => (
                        <li key={`${index}-${msgIndex}`}>{msg}</li>
                      ));
                    })
                  ) : (
                    <li>Please fill in all required fields</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {fields.map((field, index) => (
            <ParticipantFormSection
              key={field.id}
              index={index}
              quantity={quantity}
              firstName={participants[index]?.firstName || ""}
              lastName={participants[index]?.lastName || ""}
              selectedUserId={participants[index]?.userId || "other"}
              selectedPricePointId={participants[index]?.pricePointId || ""}
              allAccountUsers={allAccountUsers}
              selectedUserIds={participants.map((p) => p.userId || "other")}
              availablePricePoints={availablePricePoints}
              categoryPricePoints={categoryPricePoints}
              bookingOpen={bookingOpen}
              onFirstNameChange={(_, val) =>
                setValue(`participants.${index}.firstName`, val)
              }
              onLastNameChange={(_, val) =>
                setValue(`participants.${index}.lastName`, val)
              }
              onUserChange={(_, val) => onUserSelect(index, val)}
              onPricePointChange={(_, val) =>
                setValue(`participants.${index}.pricePointId`, val)
              }
              errors={errors.participants?.[index]}
            />
          ))}
        </div>

        <RegistrationFooter
          totalCents={calculateTotal()}
          currency={currency}
          totalLabel="Total"
          bookingOpen={bookingOpen}
          availablePricePointsLength={availablePricePoints.length}
          isFullyBooked={isFullyBookedNoPlaces}
          bookingClosesAt={event.bookingClosesAt}
          isSubmitting={isSubmitting}
          onAddAndContinue={handleSubmit((data) => onSubmit(data, true))}
          onAddAndCheckout={handleSubmit((data) => onSubmit(data, false))}
        />
      </CardContent>
    </Card>
  );
}

